import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { homepageContent, products, siteContent } from '../../data/products';
import { Reveal } from '../../motion';
import Icon from '../Icon/Icon';
import './NewDrop.css';

const NewDrop = () => {
  const content = homepageContent.newDrop;
  const featuredProduct = products.find((product) => product.active !== false && product.id === content.productId)
    || products.find((product) => product.active !== false && product.newDrop);
  const instagramUrl = siteContent.contact.instagramUrl;
  const orderLink = `https://wa.me/${siteContent.contact.whatsappNumber}`;
  const orderMessage = encodeURIComponent(
    `Hi ${siteContent.brand.name}! I'd like to order ${featuredProduct?.name || content.title}. Please share the available options, price and delivery details.`
  );
  const visualRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: visualRef,
    offset: ['start end', 'center center']
  });
  const revealOpacity = useTransform(scrollYProgress, [0.14, 0.62], [0, 1]);
  const revealY = useTransform(scrollYProgress, [0.14, 0.62], [18, 0]);
  const baseScale = useTransform(scrollYProgress, [0.14, 0.62], [1, 1.025]);

  return (
    <section id="new-drop" className="new-drop" aria-labelledby="new-drop-title">
      <div className="new-drop-inner">
        <Reveal className="new-drop-brand" y={12}>
          <img src={siteContent.brand.logo} alt={siteContent.brand.logoAlt} />
          <div>
            <span>{content.brandEyebrow}</span>
            <strong>{content.brandTitle}</strong>
          </div>
          <a href={instagramUrl} target="_blank" rel="noreferrer">
            <Icon name="instagram" size={18} />
            {content.instagramCta}
          </a>
        </Reveal>

        <div className="new-drop-grid">
          <motion.div
            ref={visualRef}
            className="new-drop-hero"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.img
              className="new-drop-image new-drop-image-base"
              src={content.baseImage}
              alt={content.imageAlt}
              loading="lazy"
              style={{ scale: reduceMotion ? 1 : baseScale }}
            />
            <motion.img
              className="new-drop-image new-drop-image-reveal"
              src={content.revealImage}
              alt=""
              loading="lazy"
              style={{
                opacity: reduceMotion ? 1 : revealOpacity,
                y: reduceMotion ? 0 : revealY
              }}
            />
            <span className="new-drop-badge">{content.badge}</span>
          </motion.div>

          <Reveal className="new-drop-copy" delay={0.08} y={20}>
            <span className="section-tag">{content.eyebrow}</span>
            <h2 id="new-drop-title" className="section-title">{content.title}</h2>
            <p>{content.body}</p>

            <dl className="new-drop-details">
              {content.details.map((detail) => (
                <div key={detail.label}>
                  <dt>{detail.label}</dt>
                  <dd>{detail.value}</dd>
                </div>
              ))}
            </dl>

            <div className="new-drop-actions">
              <a
                className="btn btn-primary"
                href={`${orderLink}?text=${orderMessage}`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="whatsapp" size={18} />
                {content.primaryCta}
              </a>
              <a className="btn btn-secondary" href="#menu">
                {content.secondaryCta}
                <Icon name="arrow" size={17} />
              </a>
            </div>
          </Reveal>
        </div>

        <div className="new-drop-stories" aria-label={`More recent ${siteContent.brand.name} bakes`}>
          {content.stories.map((bake, index) => (
            <Reveal
              as="figure"
              className="new-drop-story"
              delay={0.06 * index}
              y={16}
              key={bake.title}
            >
              <a href={instagramUrl} target="_blank" rel="noreferrer">
                <img src={bake.image} alt={bake.title} loading="lazy" />
              </a>
              <figcaption>
                <span>{bake.eyebrow}</span>
                <strong>{bake.title}</strong>
              </figcaption>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewDrop;
